/**
 * Turf (Q39): the 16×16 block a crew was cleared out of stays yours. Businesses in it pay a
 * daily tribute (economy.turf.tribute each), no gang hangs out or leans on a business there
 * while you hold it, and now and then a crew comes back for it — clear them out again within
 * the grace days or the block is theirs. Held blocks live in the world overlay (saved with the
 * seed, sent to every client), tinted on the ground and on the map.
 */
import { count as countAch } from './achievements';
import { report } from './newsfeed';
import { Overlay, cellKey, PIECE, type Turf } from '../world/overlay';
import { isBusiness } from '../world/parking';
import { spawnGangAt } from './jobs';
import type { Game, PlayerState } from './game';

/** After a clear-out at (x, y): the block is the player's (or stays theirs; taken over from another holder). */
export function claimTurf(game: Game, st: PlayerState, x: number, y: number): Turf {
  const [cx, cy] = Overlay.cellOf(x, y);
  const ov = game.world.overlay;
  const cur = ov.turf.get(cellKey(cx, cy));
  if (cur && cur.owner === st.token) { cur.contest = undefined; ov.version++; game.say('The block is yours again', st.person); return cur; }
  const t: Turf = { x: cx, y: cy, owner: st.token, name: st.name, since: game.day, owed: 0, hx: x, hy: y };
  if (cur) { const prev = [...game.players.values()].find((s) => s.token === cur.owner); if (prev) game.say(`${st.name} cleared your block — it is theirs now`, prev.person); }
  ov.setTurf(t, cx, cy);
  const n = businessesIn(game, t);
  game.say(`${cur ? `${cur.name}'s block is yours now` : 'This block is your turf now'} — ${n ? `${n} business${n === 1 ? '' : 'es'} pay $${n * game.reg.economy.turf.tribute} a day` : 'no businesses here, but no crews either'}`, st.person);
  countAch(game, st, 'turf', 1);
  report(game, 'turf', cx * 16 + 8, cy * 16 + 8, { n: String(n) }, st.name);
  return t;
}

/** Businesses (not players' property) in the block. */
export function businessesIn(game: Game, t: Turf): number {
  const seen = new Set<string>();
  for (let ty = t.y * PIECE; ty < (t.y + 1) * PIECE; ty += 2) for (let tx = t.x * PIECE; tx < (t.x + 1) * PIECE; tx += 2) {
    const b = game.world.buildingAt(tx + 0.5, ty + 0.5); if (!b || !isBusiness(b)) continue;
    const k = `${b.x},${b.y}`; if (game.props.has(k)) continue;
    seen.add(k);
  }
  return seen.size;
}

export function turfOf(game: Game, token: string): Turf[] { return [...game.world.overlay.turf.values()].filter((t) => t.owner === token); }
/** Is this spot inside a block somebody holds? */
export function heldAt(game: Game, x: number, y: number): Turf | undefined { return game.world.overlay.turfAt(x, y); }

/** Midnight: tribute in, a retake now and then, blocks lost when a contest ran out of days. */
export function dailyTurf(game: Game): void {
  const ov = game.world.overlay, eco = game.reg.economy.turf;
  let changed = false;
  for (const t of [...ov.turf.values()]) {
    const holder = [...game.players.values()].find((s) => s.token === t.owner);
    if (t.contest) {
      if (game.day - t.contest.day >= eco.graceDays) {
        ov.setTurf(undefined, t.x, t.y); changed = true;
        if (holder) game.say(`The ${game.reg.get('gang', t.contest.gang)?.name ?? 'crew'} took your block back`, holder.person);
        continue;
      }
    } else if (game.day - t.since >= eco.retakeDays && game.npcRng.chance(eco.retakeChance)) {
      const zone = game.zoneNameAt(t.hx, t.hy);
      const gangs = game.reg.all('gang').filter((g) => (g.zones[zone] ?? 0) > 0);
      const gd = gangs.length ? game.npcRng.weighted(gangs, (g) => (g.zones[zone] ?? 0) * (g.rarity ?? 1)) : undefined;
      if (gd) { t.contest = { gang: gd.id, day: game.day }; changed = true; if (holder) game.say(`The ${gd.name} are back on your block — run them off within ${eco.graceDays} days or it is theirs`, holder.person); }
    }
    const n = businessesIn(game, t);
    const made = t.contest ? 0 : n * eco.tribute;
    if (made > 0) {
      if (holder) { holder.person.items.cash = (holder.person.items.cash ?? 0) + made; countAch(game, holder, 'earned', made); countAch(game, holder, 'turfCash', made); game.say(`Your block paid $${made} tribute`, holder.person); game.events.push({ kind: 'cash', x: holder.person.x, y: holder.person.y, cls: 'in' }); }
      else { t.owed += made; changed = true; }
    }
  }
  if (changed) ov.version++;
}

/** Every step: a contesting crew appears at the old hangout when the holder (anyone) comes near; all of them dead ends the contest. */
export function stepTurf(game: Game): void {
  const ov = game.world.overlay;
  for (const t of ov.turf.values()) {
    const c = t.contest; if (!c) continue;
    const crew = game.people.filter((q) => q.gang?.id === c.gang && Math.hypot(q.gang.home[0] - t.hx, q.gang.home[1] - t.hy) < 1);
    const near = game.nearestPlayerDist(t.hx, t.hy);
    if (!c.spawned) { if (near < 70) { const made = spawnGangAt(game, { x: t.hx, y: t.hy, name: 'the block', key: cellKey(t.x, t.y) }, c.gang); if (made.length) { c.spawned = true; ov.version++; } } continue; }
    if (crew.length && crew.every((q) => q.dead)) {
      t.contest = undefined; ov.version++;
      const holder = [...game.players.values()].find((s) => s.token === t.owner);
      if (holder) game.say(`You held the block — the ${game.reg.get('gang', c.gang)?.name ?? 'crew'} are gone`, holder.person);
    } else if (!crew.length && near > 160) { c.spawned = false; ov.version++; }
  }
}

/** On join: tribute that came in while the holder was away. */
export function collectTurf(game: Game, st: PlayerState): void {
  let sum = 0;
  for (const t of turfOf(game, st.token)) { if (t.owed > 0) { sum += t.owed; t.owed = 0; } }
  if (sum > 0) { st.person.items.cash = (st.person.items.cash ?? 0) + sum; countAch(game, st, 'earned', sum); countAch(game, st, 'turfCash', sum); game.world.overlay.version++; game.say(`Your turf paid $${sum} tribute while you were away`, st.person); game.events.push({ kind: 'cash', x: st.person.x, y: st.person.y, cls: 'in' }); }
}
